import React, { useState, useEffect, useContext } from 'react';
import SEOHead from './SEOHead';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { BookOpen, ChevronRight, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlobalExamHub = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [examBanks, setExamBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasAccess = (examId) => {
    if (!user) return false;
    if (user.email === 'shifracs@gmail.com') return true;
    
    const featureName = examId === 'gre' ? 'exam_GRE_Success_Bank' : 'exam_GMAT_Success_Bank';
    
    if (user.enrolled_features && user.enrolled_features.includes(featureName)) {
      if (user.enrollment_expiry) {
        const expiry = new Date(user.enrollment_expiry);
        return expiry > new Date();
      }
    }
    return false;
  };

  useEffect(() => {
    fetchExamBanks();
  }, []);

  const fetchExamBanks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exam-bank/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const globalBanks = response.data.exam_banks.filter(bank => 
          bank.exam_id === 'gre' || bank.exam_id === 'gmat'
        );
        globalBanks.sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
        setExamBanks(globalBanks);
      }
    } catch (error) {
      console.error('Error fetching exam banks:', error);
      toast.error('Failed to load exam banks');
    } finally {
      setLoading(false);
    }
  };

  const handleExamClick = (examId) => {
    if (!user) {
      openAuth('register');
      return;
    }
    navigate(`/exam-bank/${examId}`);
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Easy': 'bg-green-100 text-green-700',
      'Medium': 'bg-yellow-100 text-yellow-700',
      'Hard': 'bg-red-100 text-red-700'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-green-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading global exam banks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="Global Exams Hub — GRE, GMAT, IELTS, TOEFL, SAT" description="Mock tests and preparation for GRE, GMAT, IELTS, TOEFL, SAT and other global standardized exams." path="/global-exam-bank" />
      {/* Hero Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-12">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 rounded-full mb-4 sm:mb-6">
              <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              <span className="text-xs sm:text-sm font-bold text-green-700">STUDY ABROAD ASPIRANTS</span>
            </div>
            
            {/* Title */}
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
              Go Global with GRE & GMAT
            </h1>
            
            {/* Subtitle */}
            <p className="text-sm sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto mb-5 sm:mb-8 px-2">
              Master the world's most recognized graduate admission tests for top universities
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xs sm:max-w-lg mx-auto mb-6 sm:mb-8">
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{examBanks.length}</p>
                <p className="text-xs sm:text-sm text-gray-500">Exams</p>
              </div>
              <div className="text-center border-x border-gray-200">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                  {examBanks.reduce((sum, bank) => sum + (bank.total_questions || 0), 0).toLocaleString()}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">Questions</p>
              </div>
              <div className="text-center">
                <p className="text-xl sm:text-3xl lg:text-4xl font-bold text-green-500">Global</p>
                <p className="text-xs sm:text-sm text-gray-500">Acceptance</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exam Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        {examBanks.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base sm:text-lg">No global exam banks available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {examBanks.map((bank) => (
              <Card
                key={bank.exam_id}
                className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-2 border-green-200 overflow-hidden"
                onClick={() => handleExamClick(bank.exam_id)}
              >
                {/* Card Header with gradient */}
                <div className="h-1.5 sm:h-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
                
                <CardContent className="p-4 sm:p-6">
                  {/* Icon and Title */}
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-green-50 rounded-xl sm:rounded-2xl flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                      🌍
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm sm:text-lg mb-0.5 sm:mb-1">{bank.display_name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{bank.description}</p>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="bg-green-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xl sm:text-2xl font-bold text-green-700">{bank.total_questions}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500">Practice Questions</p>
                      </div>
                      {bank.last_synced_at && (
                        <div className="text-right">
                          <p className="text-[10px] sm:text-xs text-gray-500">Last synced</p>
                          <p className="text-[10px] sm:text-xs text-gray-600">
                            {new Date(bank.last_synced_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Difficulty Breakdown */}
                  {bank.difficulty_breakdown && (
                    <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                      {Object.entries(bank.difficulty_breakdown).map(([level, count]) => (
                        count > 0 && (
                          <div key={level} className="flex items-center justify-between text-xs sm:text-sm">
                            <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold ${getDifficultyColor(level)}`}>
                              {level}
                            </span>
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <div className="w-20 sm:w-32 bg-gray-200 rounded-full h-1.5 sm:h-2">
                                <div
                                  className={`h-1.5 sm:h-2 rounded-full ${
                                    level === 'Easy' ? 'bg-green-500' :
                                    level === 'Medium' ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${(count / bank.total_questions) * 100}%` }}
                                />
                              </div>
                              <span className="text-[10px] sm:text-xs text-gray-600 w-12 sm:w-16 text-right">
                                {count} ({Math.round((count / bank.total_questions) * 100)}%)
                              </span>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  {/* CTA Button */}
                  {hasAccess(bank.exam_id) ? (
                    <Button 
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
                    >
                      <span>Start Practice</span>
                      <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:opacity-90 text-white font-semibold py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (isMobileApp()) {
                          openWebappWithAuth('/');
                        } else {
                          navigate('/pricing');
                        }
                      }}
                    >
                      <span>{isMobileApp() ? 'Subscribe Now' : 'Subscribe to Unlock'}</span>
                      <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-1.5 sm:ml-2" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="mb-20 sm:mb-24"></div>
      <AdvisorRibbon />
      <Footer />
    </div>
  );
};

export default GlobalExamHub;
